import { Activity, HelpCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib";

type HealthState = "healthy" | "degraded" | "unknown";

export function Footer() {
  const [health, setHealth] = useState<HealthState>("unknown");
  const appVersion = __APP_VERSION__;

  useEffect(() => {
    let mounted = true;

    async function checkHealth() {
      try {
        const data = await api.get<{ status: string }>("/health");
        if (mounted) {
          setHealth(data.status === "healthy" ? "healthy" : "degraded");
        }
      } catch {
        if (mounted) {
          setHealth("degraded");
        }
      }
    }

    checkHealth();
    const interval = setInterval(checkHealth, 60_000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  const statusColor = {
    healthy: "bg-emerald-400",
    degraded: "bg-amber-400",
    unknown: "bg-content-quaternary",
  }[health];

  const statusLabel = {
    healthy: "All systems operational",
    degraded: "Service disruption",
    unknown: "Checking status...",
  }[health];

  return (
    <footer className="shrink-0 border-t border-border-primary bg-surface-primary/50 px-6 py-2">
      <div className="flex items-center justify-between text-xs text-content-quaternary">
        {/* Left: Version + Status */}
        <div className="flex items-center gap-4">
          <span className="font-mono">v{appVersion}</span>
          <div className="flex items-center gap-1.5">
            <Activity className="h-3 w-3" />
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${statusColor}`} />
            <span>{statusLabel}</span>
          </div>
        </div>

        {/* Center: Copyright */}
        <span className="hidden sm:inline">&copy; {new Date().getFullYear()} AnthmAI. All rights reserved.</span>

        {/* Right: Links */}
        <div className="flex items-center gap-3">
          <Link to="/help" className="flex items-center gap-1 transition-colors hover:text-content-secondary">
            <HelpCircle className="h-3 w-3" />
            Help
          </Link>
        </div>
      </div>
    </footer>
  );
}
